import styled from "@emotion/styled";
import { iconSize, surface } from "@atlas-int/tokens";

// ─── Configurable asset resolution ──────────────────────────────────────────

let flagBaseUrl = "/assets/images/country_flags";
let regionBaseUrl = "/assets/images/region_icons";

export function configureFlagAssets(config: {
  flagBaseUrl?: string;
  regionBaseUrl?: string;
}) {
  if (config.flagBaseUrl) flagBaseUrl = config.flagBaseUrl;
  if (config.regionBaseUrl) regionBaseUrl = config.regionBaseUrl;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

const flagSizeMap = {
  sm: iconSize.sm,
  md: iconSize.lg,
  lg: iconSize.xl,
} as const;
type FlagSize = keyof typeof flagSizeMap;

export function resolveFlagSrc(iso3: string): string {
  return `${flagBaseUrl}/Flag-${iso3}.svg`;
}

export function resolveRegionSrc(region: string): string {
  return `${regionBaseUrl}/Icon-${region.toLowerCase()}.svg`;
}

// ─── Flag component ─────────────────────────────────────────────────────────

interface FlagProps {
  /** ISO3 code, e.g. "USA", "DEU", or special name like "Undeclared" */
  iso3: string;
  /** Size variant */
  size?: FlagSize;
  /** Show a subtle border (default true) */
  bordered?: boolean;
  /** Override the resolved src URL directly */
  src?: string;
  /** Optional base URL override (per-instance, takes precedence over global config) */
  assetBaseUrl?: string;
  className?: string;
}

const FlagImg = styled.img<{ $size: string; $bordered: boolean }>`
  width: auto;
  height: ${({ $size }) => $size};
  object-fit: contain;
  flex-shrink: 0;
  ${({ $bordered }) =>
    $bordered ? `border: 1px solid ${surface.muted};` : ""}
`;

const Flag = ({
  iso3,
  size = "md",
  bordered = true,
  src: srcProp,
  assetBaseUrl,
  className,
}: FlagProps) => {
  const src =
    srcProp ??
    (assetBaseUrl
      ? `${assetBaseUrl}/Flag-${iso3}.svg`
      : resolveFlagSrc(iso3));

  return (
    <FlagImg
      src={src}
      alt={`Flag of ${iso3}`}
      $size={flagSizeMap[size]}
      $bordered={bordered}
      className={className}
    />
  );
};

// ─── RegionIcon component ───────────────────────────────────────────────────

interface RegionIconProps {
  /** Region name: "africa" | "americas" | "asia" | "europe" | "oceania" */
  region: string;
  /** Size variant */
  size?: FlagSize;
  /** Override the resolved src URL directly */
  src?: string;
  /** Optional base URL override (per-instance, takes precedence over global config) */
  assetBaseUrl?: string;
  className?: string;
}

const RegionImg = styled.img<{ $size: string }>`
  width: auto;
  height: ${({ $size }) => $size};
  object-fit: contain;
  flex-shrink: 0;
`;

const RegionIcon = ({
  region,
  size = "md",
  src: srcProp,
  assetBaseUrl,
  className,
}: RegionIconProps) => {
  const src =
    srcProp ??
    (assetBaseUrl
      ? `${assetBaseUrl}/Icon-${region.toLowerCase()}.svg`
      : resolveRegionSrc(region));

  return (
    <RegionImg
      src={src}
      alt={`${region} region`}
      $size={flagSizeMap[size]}
      className={className}
    />
  );
};

export { Flag, RegionIcon };
export type { FlagProps, RegionIconProps, FlagSize };
